import { useState } from "react";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { ArrowLeft, Pencil, Plus, CalendarPlus, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { deletePatient, getPatient } from "@/lib/api/patients";
import { listProfessionals } from "@/lib/api/professionals";
import { listVisits } from "@/lib/api/visits";
import { PatientDialog } from "@/components/patient-dialog";
import { VisitDialog } from "@/components/visit-dialog";
import type { Visit } from "@/lib/types";

export const Route = createFileRoute("/pacientes/$id")({
  head: () => ({
    meta: [
      { title: "Ficha de paciente · Rafael González" },
      { name: "description", content: "Ficha, datos de contacto e historial clínico del paciente." },
    ],
  }),
  component: PatientDetailPage,
});

const statusLabel: Record<Visit["status"], { label: string; className: string }> = {
  scheduled: { label: "Agendada", className: "bg-secondary text-secondary-foreground" },
  completed: { label: "Realizada", className: "bg-primary/15 text-primary" },
  cancelled: { label: "Cancelada", className: "bg-destructive/15 text-destructive" },
};

function PatientDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [visitOpen, setVisitOpen] = useState(false);
  const [editingVisit, setEditingVisit] = useState<Visit | null>(null);

  const { data: patient, isLoading } = useQuery({ queryKey: ["patients", id], queryFn: () => getPatient(id) });
  const { data: visits = [], isLoading: visitsLoading } = useQuery({ queryKey: ["visits", { patientId: id }], queryFn: () => listVisits({ patientId: id }) });
  const { data: professionals = [] } = useQuery({ queryKey: ["professionals"], queryFn: () => listProfessionals() });

  const deleteMutation = useMutation({
    mutationFn: () => deletePatient(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["patients"] });
      toast.success("Paciente eliminado");
      navigate({ to: "/pacientes" });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const profById = Object.fromEntries(professionals.map((p) => [p.id, p]));

  if (isLoading) return <div className="text-muted-foreground">Cargando...</div>;
  if (!patient) return <div className="text-muted-foreground">Paciente no encontrado</div>;

  const orderedVisits = [...visits].sort((a, b) => b.start.localeCompare(a.start));

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div className="flex items-center justify-between gap-3">
        <Button asChild variant="ghost" size="sm">
          <Link to="/pacientes"><ArrowLeft className="mr-2 h-4 w-4" /> Volver</Link>
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setEditOpen(true)}>
            <Pencil className="mr-2 h-4 w-4" /> Editar
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button variant="outline"><Trash2 className="mr-2 h-4 w-4" /> Eliminar</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>¿Eliminar paciente?</AlertDialogTitle>
                <AlertDialogDescription>
                  {visitsLoading
                    ? "Comprobando las citas asociadas..."
                    : visits.length > 0
                      ? "No se puede eliminar este paciente porque tiene citas asignadas."
                      : "Esta acción no se puede deshacer."}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancelar</AlertDialogCancel>
                <AlertDialogAction
                  disabled={visitsLoading || visits.length > 0 || deleteMutation.isPending}
                  onClick={() => deleteMutation.mutate()}
                >
                  {deleteMutation.isPending ? "Eliminando..." : "Eliminar"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button onClick={() => { setEditingVisit(null); setVisitOpen(true); }}>
            <CalendarPlus className="mr-2 h-4 w-4" /> Nueva cita
          </Button>
        </div>
      </div>

      <div>
        <h1 className="text-2xl font-semibold tracking-tight">{patient.firstName} {patient.lastName}</h1>
        <p className="text-sm text-muted-foreground">
          DNI {patient.dni} · Alta {format(new Date(patient.createdAt), "d MMM yyyy", { locale: es })}
        </p>
      </div>

      <Tabs defaultValue="datos">
        <TabsList>
          <TabsTrigger value="datos">Datos</TabsTrigger>
          <TabsTrigger value="historial">Historial ({orderedVisits.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="datos" className="mt-4">
          <Card>
            <CardContent className="grid grid-cols-1 gap-4 pt-6 sm:grid-cols-2">
              <Field label="Nombre" value={patient.firstName} />
              <Field label="Apellidos" value={patient.lastName} />
              <Field label="DNI" value={patient.dni} />
              <Field label="Fecha de nacimiento" value={patient.birthDate ? format(new Date(patient.birthDate), "d MMM yyyy", { locale: es }) : "—"} />
              <Field label="Teléfono" value={patient.phone} />
              <Field label="Email" value={patient.email} />
              <Field label="Dirección" value={patient.address || "—"} className="sm:col-span-2" />
              <Field label="Notas" value={patient.notes || "—"} className="sm:col-span-2" />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="historial" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-base">Historial de citas</CardTitle>
              <Button size="sm" variant="ghost" onClick={() => { setEditingVisit(null); setVisitOpen(true); }}>
                <Plus className="mr-1 h-4 w-4" /> Añadir
              </Button>
            </CardHeader>
            <CardContent>
              {orderedVisits.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">Sin citas registradas.</p>
              ) : (
                <ul className="divide-y">
                  {orderedVisits.map((v) => {
                    const prof = profById[v.professionalId];
                    const s = statusLabel[v.status];
                    return (
                      <li
                        key={v.id}
                        className="cursor-pointer space-y-1 px-1 py-3 hover:bg-muted/50"
                        onClick={() => { setEditingVisit(v); setVisitOpen(true); }}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="flex items-center gap-3">
                            <span className="text-sm font-medium">
                              {format(new Date(v.start), "d MMM yyyy · HH:mm", { locale: es })}
                            </span>
                            <Badge className={s.className} variant="secondary">{s.label}</Badge>
                            {v.isInvoiced && (
                              <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400" variant="secondary">Facturada</Badge>
                            )}
                          </div>
                          {prof && (
                            <Badge variant="outline" className="gap-2">
                              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: prof.color }} />
                              {prof.firstName} {prof.lastName}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm">{v.reason}</div>
                        {v.notes && <div className="text-sm text-muted-foreground">{v.notes}</div>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <PatientDialog open={editOpen} onOpenChange={setEditOpen} patient={patient} />
      <VisitDialog
        open={visitOpen}
        onOpenChange={setVisitOpen}
        visit={editingVisit}
        defaultPatientId={patient.id}
      />
    </div>
  );
}

function Field({ label, value, className }: { label: string; value: string; className?: string }) {
  return (
    <div className={className}>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1 text-sm">{value}</div>
    </div>
  );
}
